import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconRoad,
  IconRulerMeasure,
  IconCamera,
  IconCircleCheck,
  IconTool,
} from '@tabler/icons-react';

interface RequirementSuggestion {
  value: string;
  category: string;
  icon: React.ComponentType<{
    size?: number;
    stroke?: number;
    color?: string;
    className?: string;
  }>;
}

interface AutocompleteOption {
  value: string;
  label: string;
  category: string;
  icon: React.ComponentType<{
    size?: number;
    stroke?: number;
    color?: string;
    className?: string;
  }>;
}

interface FilterOptions {
  options: AutocompleteOption[];
  search: string;
}

interface UseRequirementSuggestionsReturn {
  // Suggestion data
  requirementSuggestions: RequirementSuggestion[];
  autocompleteData: AutocompleteOption[];
  categoryColors: Record<string, string>;

  // Filter function
  filterSuggestions: ({
    options,
    search,
  }: FilterOptions) => AutocompleteOption[];

  // Render helper
  getSuggestionIconColor: (category?: string) => string;
}

/**
 * Custom hook for managing requirement suggestions in the task wizard
 * Extracts suggestion data generation, category mapping, and filtering logic
 * from Step3Requirements component
 *
 * Features:
 * - Dynamic suggestion generation with i18n support
 * - Category-based color mapping for visual organization
 * - Intelligent filtering for autocomplete functionality
 * - Memoized data for optimal performance
 * - Proper TypeScript typing for all suggestion data
 */
export const useRequirementSuggestions =
  (): UseRequirementSuggestionsReturn => {
    const { t } = useTranslation();

    // Memoized requirement suggestions with dynamic translations
    const requirementSuggestions = useMemo(
      (): RequirementSuggestion[] => [
        // Inspection Category
        {
          value: t('wizard.step3.suggestions.roadSurfaceAssessment'),
          category: t('wizard.step3.categories.inspection'),
          icon: IconRoad,
        },
        {
          value: t('wizard.step3.suggestions.horizontalMarkingControl'),
          category: t('wizard.step3.categories.inspection'),
          icon: IconRoad,
        },
        {
          value: t('wizard.step3.suggestions.shoulderConditionCheck'),
          category: t('wizard.step3.categories.inspection'),
          icon: IconRoad,
        },
        {
          value: t('wizard.step3.suggestions.curbstoneConditionAssessment'),
          category: t('wizard.step3.categories.inspection'),
          icon: IconRoad,
        },

        // Measurements Category
        {
          value: t('wizard.step3.suggestions.potholeDepthMeasurement'),
          category: t('wizard.step3.categories.measurements'),
          icon: IconRulerMeasure,
        },
        {
          value: t('wizard.step3.suggestions.crackWidthMeasurement'),
          category: t('wizard.step3.categories.measurements'),
          icon: IconRulerMeasure,
        },
        {
          value: t('wizard.step3.suggestions.surfaceEvennessControl'),
          category: t('wizard.step3.categories.measurements'),
          icon: IconRulerMeasure,
        },
        {
          value: t('wizard.step3.suggestions.crossSlopeMeasurement'),
          category: t('wizard.step3.categories.measurements'),
          icon: IconRulerMeasure,
        },

        // Documentation Category
        {
          value: t('wizard.step3.suggestions.damagePhotoDocumentation'),
          category: t('wizard.step3.categories.documentation'),
          icon: IconCamera,
        },
        {
          value: t('wizard.step3.suggestions.inspectionReportPreparation'),
          category: t('wizard.step3.categories.documentation'),
          icon: IconCamera,
        },
        {
          value: t('wizard.step3.suggestions.gpsLocationDocumentation'),
          category: t('wizard.step3.categories.documentation'),
          icon: IconCamera,
        },
        {
          value: t('wizard.step3.suggestions.driveVideoRecording'),
          category: t('wizard.step3.categories.documentation'),
          icon: IconCamera,
        },

        // Acceptance Category
        {
          value: t('wizard.step3.suggestions.workAcceptanceProtocol'),
          category: t('wizard.step3.categories.acceptance'),
          icon: IconCircleCheck,
        },
        {
          value: t('wizard.step3.suggestions.projectComplianceCheck'),
          category: t('wizard.step3.categories.acceptance'),
          icon: IconCircleCheck,
        },
        {
          value: t('wizard.step3.suggestions.workQualityControl'),
          category: t('wizard.step3.categories.acceptance'),
          icon: IconCircleCheck,
        },
        {
          value: t('wizard.step3.suggestions.acceptanceDocumentsSigning'),
          category: t('wizard.step3.categories.acceptance'),
          icon: IconCircleCheck,
        },

        // Repairs Category
        {
          value: t('wizard.step3.suggestions.localPotholeRepair'),
          category: t('wizard.step3.categories.repairs'),
          icon: IconTool,
        },
        {
          value: t('wizard.step3.suggestions.markingSupplement'),
          category: t('wizard.step3.categories.repairs'),
          icon: IconTool,
        },
        {
          value: t('wizard.step3.suggestions.surfaceCleaning'),
          category: t('wizard.step3.categories.repairs'),
          icon: IconTool,
        },
        {
          value: t('wizard.step3.suggestions.drainGrateAdjustment'),
          category: t('wizard.step3.categories.repairs'),
          icon: IconTool,
        },
      ],
      [t]
    );

    // Memoized color mapping for requirement categories
    const categoryColors = useMemo(
      (): Record<string, string> => ({
        [t('wizard.step3.categories.inspection')]:
          'var(--mantine-primary-color-5)',
        [t('wizard.step3.categories.measurements')]:
          'var(--mantine-color-orange-5)',
        [t('wizard.step3.categories.documentation')]:
          'var(--mantine-color-violet-5)',
        [t('wizard.step3.categories.acceptance')]:
          'var(--mantine-color-green-5)',
        [t('wizard.step3.categories.repairs')]: 'var(--mantine-color-yellow-6)',
      }),
      [t]
    );

    // Memoized autocomplete data prepared for Mantine Autocomplete component
    const autocompleteData = useMemo(
      (): AutocompleteOption[] =>
        requirementSuggestions.map((suggestion) => ({
          value: suggestion.value,
          label: suggestion.value,
          category: suggestion.category,
          icon: suggestion.icon,
        })),
      [requirementSuggestions]
    );

    // Intelligent filter function for autocomplete suggestions
    const filterSuggestions = useMemo(
      () =>
        ({ options, search }: FilterOptions): AutocompleteOption[] => {
          const searchLower = search.toLowerCase().trim();

          if (!searchLower) {
            return options;
          }

          return options.filter((item) => {
            // Check if item has a 'value' property (it's a regular option, not a group)
            if ('value' in item && typeof item.value === 'string') {
              const optionItem = item as AutocompleteOption;

              // Search in both value text and category
              return (
                optionItem.value.toLowerCase().includes(searchLower) ||
                (optionItem.category &&
                  optionItem.category.toLowerCase().includes(searchLower))
              );
            }
            return false;
          });
        },
      []
    );

    // Helper function to get icon color for a category
    const getSuggestionIconColor = useMemo(
      () =>
        (category?: string): string => {
          return category
            ? categoryColors[category] || 'var(--mantine-color-gray-5)'
            : 'var(--mantine-color-gray-5)';
        },
      [categoryColors]
    );

    return {
      // Suggestion data
      requirementSuggestions,
      autocompleteData,
      categoryColors,

      // Filter function
      filterSuggestions,

      // Render helper
      getSuggestionIconColor,
    };
  };
